import { Badge, withStyles } from '@material-ui/core'
import ChannelIcon from 'app/componentV4/Channel/ChannelIcon'
import Restricted from 'app/componentV4/Restricted'
import TruncateWithTooltip from 'app/componentV4/Table/TruncateWithTooltip'
import WarningRoundedIcon from '@material-ui/icons/WarningRounded'
import clsx from 'clsx'
import Avatars from 'app/componentV4/Avatar/Avatars'
import { head } from 'ramda'
import { resizeAvatar_80 } from 'commons/resizeImage'
import GroupSubscriberTableAction from './GroupSubscriberTableAction'
import StatusChip from 'app/componentV4/Status/Chip'
import { subscriberStatus } from 'app/constants/subscriberCRM'
import SubscriberTooltip from './SubscriberTooltip'
import LimitTooltip from 'app/componentV4/Tooltip/LimitTooltip'
import { getLocalizedDate } from 'commons/date'

const StyledBadge = withStyles({
  badge: {
    width: '25px',
    padding: 0,
    alignContent: 'flex-end',
    justifyContent: 'flex-end',
  },
})(Badge)

export const ColumnOfSubscriberData = (
  isDownXs,
  openDeleteSubscriberFromGroupDialog,
  formatMessage,
  group,
  hasPermissionCreateAndDelete,
) => {
  const columns = [
    {
      header: formatMessage({ id: 'firstName.label' }),
      accessor: 'firstName',
      style: {
        minWidth: '200px',
        maxWidth: '230px',
        left: isDownXs ? 0 : 62,
        position: isDownXs ? 'unset' : 'sticky',
      },
      sort: true,
      render: (row) => (
        <div className="flex w-fit">
          <SubscriberTooltip
            subscriber={row}
            text={
              <div className="flex space-x-3">
                <StyledBadge
                  overlap="circle"
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  badgeContent={
                    <WarningRoundedIcon
                      fontSize="small"
                      style={{ color: '#D97706' }}
                      className={clsx(!row.hasBadContact && 'hidden')}
                    />
                  }
                >
                  <Avatars
                    id={row.id}
                    name={`${row.firstName} ${row.lastName}`}
                    src={resizeAvatar_80(row.avatar)}
                    characters={
                      row.avatarCharacters ??
                      `${head(row.firstName)}${head(row.lastName)}`
                    }
                    className={'w-32 h-32 text-14 font-bold'}
                  />
                </StyledBadge>
                <div className="flex items-center w-full">
                  <LimitTooltip
                    maxWidth={150}
                    title={row.firstName}
                    className="truncate"
                  />
                </div>
              </div>
            }
          />
        </div>
      ),
    },
    {
      header: formatMessage({ id: 'lastName.label' }),
      accessor: 'lastName',
      style: {
        minWidth: '150px',
      },
      sort: true,
      render: (row) => (
        <LimitTooltip
          maxWidth={150}
          title={row.lastName}
          className="truncate"
        />
      ),
    },
    {
      header: formatMessage({ id: 'groups.header.label' }),
      accessor: 'groups',
      style: { minWidth: '200px', maxWidth: '230px', zIndex: 0 },
      sort: false,
      render: (row) => (
        <TruncateWithTooltip
          value={row.tags.map((item) => item.name).join(', ')}
          className="truncate"
          placement="bottom-start"
        />
      ),
    },
    {
      header: formatMessage({ id: 'subscriber.text.messageChannel' }),
      accessor: 'channel',
      style: {
        minWidth: '100px',
      },
      sort: true,
      render: (row) => {
        return <ChannelIcon channel={Number(row.channel)} isTooltip={true} />
      },
    },
    {
      header: formatMessage({ id: 'email.address.label' }),
      accessor: 'email',
      style: { minWidth: '180px', maxWidth: '230px', zIndex: 0 },
      sort: true,
      render: (row) => {
        return row.hideContacts === 1 ? (
          <Restricted />
        ) : (
          <TruncateWithTooltip value={row.email} className="truncate" />
        )
      },
    },
    {
      header: formatMessage({ id: 'subscriber.label.phoneNumber' }),
      accessor: 'phoneNumber',
      style: { minWidth: '180px', maxWidth: '230px', zIndex: 0 },
      sort: true,
      render: (row) => {
        return row.hideContacts === 1 ? (
          <Restricted />
        ) : (
          <TruncateWithTooltip value={row.phoneNumber} className="truncate" />
        )
      },
    },
  ]
  if (hasPermissionCreateAndDelete) {
    columns.push({
      header: '',
      action: true,
      render: (row) => (
        <>
          <GroupSubscriberTableAction
            subscriber={row}
            group={group}
            onOpenDeleteGroupDialog={openDeleteSubscriberFromGroupDialog}
          />
        </>
      ),
    })
  }
  return columns
}

export const ColumnOfSubscriberWitoutGroup = (isDownXs, formatMessage) => {
  const columns = [
    {
      header: formatMessage({ id: 'firstName.label' }),
      accessor: 'firstName',
      style: {
        minWidth: '200px',
        maxWidth: '230px',
        left: isDownXs ? 0 : 62,
        position: isDownXs ? 'unset' : 'sticky',
      },
      sort: true,
      render: (row) => (
        <div className="flex space-x-3">
          <StyledBadge
            overlap="circle"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            badgeContent={
              <WarningRoundedIcon
                fontSize="small"
                style={{ color: '#D97706' }}
                className={clsx(!row.hasBadContact && 'hidden')}
              />
            }
          >
            <Avatars
              id={row.id}
              name={`${row.firstName} ${row.lastName}`}
              src={resizeAvatar_80(row.avatar)}
              characters={
                row.avatarCharacters ??
                `${head(row.firstName)}${head(row.lastName)}`
              }
              className={'w-32 h-32 text-14 font-bold'}
            />
          </StyledBadge>
          <TruncateWithTooltip
            value={row.firstName}
            className="my-auto truncate"
          />
        </div>
      ),
    },
    {
      header: formatMessage({ id: 'lastName.label' }),
      accessor: 'lastName',
      style: {
        minWidth: '100px',
      },
      sort: true,
      render: (row) => (
        <LimitTooltip
          maxWidth={100}
          title={row.lastName}
          className="truncate"
        />
      ),
    },
    {
      header: formatMessage({ id: 'text.status' }),
      accessor: 'status',
      style: {
        minWidth: '150px',
      },
      sort: true,
      render: ({ lastInvitationStatus }) => (
        <StatusChip
          type={subscriberStatus[lastInvitationStatus].type}
          label={formatMessage({
            id: subscriberStatus[lastInvitationStatus].label,
          })}
        />
      ),
    },
    {
      header: formatMessage({ id: 'addedDate.label' }),
      accessor: 'createdAt',
      style: {
        minWidth: '150px',
      },
      sort: true,
      render: (row) => {
        getLocalizedDate(row.createdAt)
      },
    },
  ]

  return columns
}
